import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Result } from '../models/core/result.model';
import { Pavement } from '../models/operation/pavement.model';
import { Bollard } from '../models/operation/bollard.model';
import { Bridge } from '../models/operation/bridge.model';
import { BusStopModel } from '../models/operation/bus-stop.model';
import { DrainageCover } from '../models/operation/drainage-cover.model';
import { Drainage } from '../models/operation/drainage.model';
import { Fence } from '../models/operation/fence.model';
import { GeoTechStructure } from '../models/operation/geo-tech-structure.model';
import { Guardrail } from '../models/operation/guardrail.model';
import { MultipurposeHall } from '../models/operation/multipurpose-hall.model';
import { Parking } from '../models/operation/parking.model';
import { PublicToilet } from '../models/operation/public-toilet.model';
import { RoadKerb } from '../models/operation/road-kerb.model';
import { RoadMarking } from '../models/operation/road-marking.model';
import { TrafficLight } from '../models/operation/traffic-light.model';
import { TrafficSign } from '../models/operation/traffic-sign.model';
import { Walkway } from '../models/operation/walkway.model';
import { BinCentre } from '../models/operation/bin-centre.model';
import { environment } from '../../../environments/environment';
import { ColumnFilters } from '../models/ui/table-column.model';

/** UI assetType (singular) → list/export API path slug */
export const ASSET_TYPE_TO_API_ENDPOINT: Record<string, string> = {
    pavement: 'pavements',
    bollard: 'bollards',
    bridge: 'bridges',
    bus_stop: 'bus_stops',
    drainage_cover: 'drainage_covers',
    drainage: 'drainages',
    fence: 'fences',
    geo_tech_structure: 'geo_tech_structures',
    guardrail: 'guard_rails',
    multipurpose_hall: 'multipurpose_halls',
    parking: 'parking',
    public_toilet: 'public_toilets',
    road_kerb: 'road_kerbs',
    road_marking: 'road_markings',
    traffic_light: 'traffic_lights',
    traffic_sign: 'traffic_signs',
    walkway: 'walkways',
    bin_centre: 'bin_centres',
};

export type AssetExportFormat = 'csv' | 'xlsx';

export interface AssetExportColumn {
    field: string;
    header: string;
}

@Injectable({
    providedIn: 'root'
})
export class AssetsService {
    private http = inject(HttpClient);
    private apiUrl = `${environment.apiUrl}/assets`;

    // Helper
    private buildListParams(page: number, size: number, filters: ColumnFilters = {}): HttpParams {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('size', size.toString());

        for (const [key, value] of Object.entries(filters || {})) {
            if (value?.trim()) {
                params = params.set(key, value.trim());
            }
        }

        return params;
    }

    private buildFilterParams(filters: ColumnFilters = {}): HttpParams {
        let params = new HttpParams();
        for (const [key, value] of Object.entries(filters || {})) {
            if (value?.trim()) {
                params = params.set(key, value.trim());
            }
        }
        return params;
    }

    private find<T>(endpoint: string, page: number, size: number, filters: ColumnFilters = {}): Observable<Result<T[]>> {
        return this.http.get<Result<T[]>>(`${this.apiUrl}/${endpoint}`, {
            params: this.buildListParams(page, size, filters)
        });
    }

    private create<T>(endpoint: string, item: any): Observable<T> {
        return this.http.post<T>(`${this.apiUrl}/${endpoint}`, item);
    }

    private update<T>(endpoint: string, id: number, item: any): Observable<T> {
        return this.http.put<T>(`${this.apiUrl}/${endpoint}/${id}`, item);
    }

    private delete(endpoint: string, id: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${endpoint}/${id}`);
    }

    // Pavement
    findPavements(page: number, size: number, filters: ColumnFilters = {}) { return this.find<Pavement>('pavements', page, size, filters); }
    getPavement(id: number): Observable<Pavement> { return this.http.get<Pavement>(`${this.apiUrl}/pavements/${id}`); }
    createPavement(item: Pavement) { return this.create<Pavement>('pavements', item); }
    updatePavement(id: number, item: Pavement) { return this.update<Pavement>('pavements', id, item); }
    deletePavement(id: number) { return this.delete('pavements', id); }

    // Bollard
    findBollards(page: number, size: number, filters: ColumnFilters = {}) { return this.find<Bollard>('bollards', page, size, filters); }
    getBollard(id: number): Observable<Bollard> { return this.http.get<Bollard>(`${this.apiUrl}/bollards/${id}`); }
    createBollard(item: Bollard) { return this.create<Bollard>('bollards', item); }
    updateBollard(id: number, item: Bollard) { return this.update<Bollard>('bollards', id, item); }
    deleteBollard(id: number) { return this.delete('bollards', id); }

    // Bridge
    findBridges(page: number, size: number, filters: ColumnFilters = {}) { return this.find<Bridge>('bridges', page, size, filters); }
    getBridge(id: number): Observable<Bridge> { return this.http.get<Bridge>(`${this.apiUrl}/bridges/${id}`); }
    createBridge(item: Bridge) { return this.create<Bridge>('bridges', item); }
    updateBridge(id: number, item: Bridge) { return this.update<Bridge>('bridges', id, item); }
    deleteBridge(id: number) { return this.delete('bridges', id); }

    // BusStop
    findBusStops(page: number, size: number, filters: ColumnFilters = {}) { return this.find<BusStopModel>('bus_stops', page, size, filters); }
    getBusStop(id: number): Observable<BusStopModel> { return this.http.get<BusStopModel>(`${this.apiUrl}/bus_stops/${id}`); }
    createBusStop(item: BusStopModel) { return this.create<BusStopModel>('bus_stops', item); }
    updateBusStop(id: number, item: BusStopModel) { return this.update<BusStopModel>('bus_stops', id, item); }
    deleteBusStop(id: number) { return this.delete('bus_stops', id); }

    // DrainageCover
    findDrainageCovers(page: number, size: number, filters: ColumnFilters = {}) { return this.find<DrainageCover>('drainage_covers', page, size, filters); }
    getDrainageCover(id: number): Observable<DrainageCover> { return this.http.get<DrainageCover>(`${this.apiUrl}/drainage_covers/${id}`); }
    createDrainageCover(item: DrainageCover) { return this.create<DrainageCover>('drainage_covers', item); }
    updateDrainageCover(id: number, item: DrainageCover) { return this.update<DrainageCover>('drainage_covers', id, item); }
    deleteDrainageCover(id: number) { return this.delete('drainage_covers', id); }

    // Drainage
    findDrainages(page: number, size: number, filters: ColumnFilters = {}) { return this.find<Drainage>('drainages', page, size, filters); }
    getDrainage(id: number): Observable<Drainage> { return this.http.get<Drainage>(`${this.apiUrl}/drainages/${id}`); }
    createDrainage(item: Drainage) { return this.create<Drainage>('drainages', item); }
    updateDrainage(id: number, item: Drainage) { return this.update<Drainage>('drainages', id, item); }
    deleteDrainage(id: number) { return this.delete('drainages', id); }

    // Fence
    findFences(page: number, size: number, filters: ColumnFilters = {}) { return this.find<Fence>('fences', page, size, filters); }
    getFence(id: number): Observable<Fence> { return this.http.get<Fence>(`${this.apiUrl}/fences/${id}`); }
    createFence(item: Fence) { return this.create<Fence>('fences', item); }
    updateFence(id: number, item: Fence) { return this.update<Fence>('fences', id, item); }
    deleteFence(id: number) { return this.delete('fences', id); }

    // GeoTechStructure
    findGeoTechStructures(page: number, size: number, filters: ColumnFilters = {}) { return this.find<GeoTechStructure>('geo_tech_structures', page, size, filters); }
    getGeoTechStructure(id: number): Observable<GeoTechStructure> { return this.http.get<GeoTechStructure>(`${this.apiUrl}/geo_tech_structures/${id}`); }
    createGeoTechStructure(item: GeoTechStructure) { return this.create<GeoTechStructure>('geo_tech_structures', item); }
    updateGeoTechStructure(id: number, item: GeoTechStructure) { return this.update<GeoTechStructure>('geo_tech_structures', id, item); }
    deleteGeoTechStructure(id: number) { return this.delete('geo_tech_structures', id); }

    // Guardrail
    findGuardrails(page: number, size: number, filters: ColumnFilters = {}) { return this.find<Guardrail>('guard_rails', page, size, filters); }
    getGuardrail(id: number): Observable<Guardrail> { return this.http.get<Guardrail>(`${this.apiUrl}/guard_rails/${id}`); }
    createGuardrail(item: Guardrail) { return this.create<Guardrail>('guard_rails', item); }
    updateGuardrail(id: number, item: Guardrail) { return this.update<Guardrail>('guard_rails', id, item); }
    deleteGuardrail(id: number) { return this.delete('guard_rails', id); }

    // MultipurposeHall
    findMultipurposeHalls(page: number, size: number, filters: ColumnFilters = {}) { return this.find<MultipurposeHall>('multipurpose_halls', page, size, filters); }
    getMultipurposeHall(id: number): Observable<MultipurposeHall> { return this.http.get<MultipurposeHall>(`${this.apiUrl}/multipurpose_halls/${id}`); }
    createMultipurposeHall(item: MultipurposeHall) { return this.create<MultipurposeHall>('multipurpose_halls', item); }
    updateMultipurposeHall(id: number, item: MultipurposeHall) { return this.update<MultipurposeHall>('multipurpose_halls', id, item); }
    deleteMultipurposeHall(id: number) { return this.delete('multipurpose_halls', id); }

    // Parking
    findParking(page: number, size: number, filters: ColumnFilters = {}) { return this.find<Parking>('parking', page, size, filters); }
    getParking(id: number): Observable<Parking> { return this.http.get<Parking>(`${this.apiUrl}/parking/${id}`); }
    createParking(item: Parking) { return this.create<Parking>('parking', item); }
    updateParking(id: number, item: Parking) { return this.update<Parking>('parking', id, item); }
    deleteParking(id: number) { return this.delete('parking', id); }

    // PublicToilet
    findPublicToilets(page: number, size: number, filters: ColumnFilters = {}) { return this.find<PublicToilet>('public_toilets', page, size, filters); }
    getPublicToilet(id: number): Observable<PublicToilet> { return this.http.get<PublicToilet>(`${this.apiUrl}/public_toilets/${id}`); }
    createPublicToilet(item: PublicToilet) { return this.create<PublicToilet>('public_toilets', item); }
    updatePublicToilet(id: number, item: PublicToilet) { return this.update<PublicToilet>('public_toilets', id, item); }
    deletePublicToilet(id: number) { return this.delete('public_toilets', id); }

    // RoadKerb
    findRoadKerbs(page: number, size: number, filters: ColumnFilters = {}) { return this.find<RoadKerb>('road_kerbs', page, size, filters); }
    getRoadKerb(id: number): Observable<RoadKerb> { return this.http.get<RoadKerb>(`${this.apiUrl}/road_kerbs/${id}`); }
    createRoadKerb(item: RoadKerb) { return this.create<RoadKerb>('road_kerbs', item); }
    updateRoadKerb(id: number, item: RoadKerb) { return this.update<RoadKerb>('road_kerbs', id, item); }
    deleteRoadKerb(id: number) { return this.delete('road_kerbs', id); }

    // RoadMarking
    findRoadMarkings(page: number, size: number, filters: ColumnFilters = {}) { return this.find<RoadMarking>('road_markings', page, size, filters); }
    getRoadMarking(id: number): Observable<RoadMarking> { return this.http.get<RoadMarking>(`${this.apiUrl}/road_markings/${id}`); }
    createRoadMarking(item: RoadMarking) { return this.create<RoadMarking>('road_markings', item); }
    updateRoadMarking(id: number, item: RoadMarking) { return this.update<RoadMarking>('road_markings', id, item); }
    deleteRoadMarking(id: number) { return this.delete('road_markings', id); }

    // TrafficLight
    findTrafficLights(page: number, size: number, filters: ColumnFilters = {}) { return this.find<TrafficLight>('traffic_lights', page, size, filters); }
    getTrafficLight(id: number): Observable<TrafficLight> { return this.http.get<TrafficLight>(`${this.apiUrl}/traffic_lights/${id}`); }
    createTrafficLight(item: TrafficLight) { return this.create<TrafficLight>('traffic_lights', item); }
    updateTrafficLight(id: number, item: TrafficLight) { return this.update<TrafficLight>('traffic_lights', id, item); }
    deleteTrafficLight(id: number) { return this.delete('traffic_lights', id); }

    // TrafficSign
    findTrafficSigns(page: number, size: number, filters: ColumnFilters = {}) { return this.find<TrafficSign>('traffic_signs', page, size, filters); }
    getTrafficSign(id: number): Observable<TrafficSign> { return this.http.get<TrafficSign>(`${this.apiUrl}/traffic_signs/${id}`); }
    createTrafficSign(item: TrafficSign) { return this.create<TrafficSign>('traffic_signs', item); }
    updateTrafficSign(id: number, item: TrafficSign) { return this.update<TrafficSign>('traffic_signs', id, item); }
    deleteTrafficSign(id: number) { return this.delete('traffic_signs', id); }

    // Walkway
    findWalkways(page: number, size: number, filters: ColumnFilters = {}) { return this.find<Walkway>('walkways', page, size, filters); }
    getWalkway(id: number): Observable<Walkway> { return this.http.get<Walkway>(`${this.apiUrl}/walkways/${id}`); }
    createWalkway(item: Walkway) { return this.create<Walkway>('walkways', item); }
    updateWalkway(id: number, item: Walkway) { return this.update<Walkway>('walkways', id, item); }
    deleteWalkway(id: number) { return this.delete('walkways', id); }

    // BinCentre
    findBinCentres(page: number, size: number, filters: ColumnFilters = {}) { return this.find<BinCentre>('bin_centres', page, size, filters); }
    getBinCentre(id: number): Observable<BinCentre> { return this.http.get<BinCentre>(`${this.apiUrl}/bin_centres/${id}`); }
    createBinCentre(item: BinCentre) { return this.create<BinCentre>('bin_centres', item); }
    updateBinCentre(id: number, item: BinCentre) { return this.update<BinCentre>('bin_centres', id, item); }
    deleteBinCentre(id: number) { return this.delete('bin_centres', id); }

    // Generic find by asset type endpoint (e.g. bollards, pavements)
    findAssetsByType<T = any>(assetType: string, page: number, size: number, filters: ColumnFilters = {}): Observable<Result<T>> {
        return this.http.get<Result<T>>(`${this.apiUrl}/${assetType}`, {
            params: this.buildListParams(page, size, filters)
        });
    }

    // MaintenanceAsset - Dynamic methods with assetType parameter
    findMaintenanceAssets(assetType: string, page: number, size: number, filters: ColumnFilters = {}, assetId?: number | null): Observable<Result<any[]>> {
        let params = new HttpParams()
            .set('asset_type', assetType)
            .set('page', page.toString())
            .set('size', size.toString());

        for (const [key, value] of Object.entries(filters || {})) {
            if (value?.trim()) {
                params = params.set(key, value.trim());
            }
        }

        if (assetId) {
            params = params.set('asset_id', assetId.toString());
        }

        return this.http.get<Result<any[]>>(`${this.apiUrl}/maintenance`, { params }).pipe(
            map(res => {
                if (res.data) {
                    res.data = res.data.map(item => this.mapMaintenanceRecord(item));
                }
                return res;
            })
        );
    }

    getMaintenanceAsset(assetType: string, id: number): Observable<any> {
        const params = new HttpParams().set('asset_type', assetType);
        return this.http.get<any>(`${this.apiUrl}/maintenance/${id}`, { params }).pipe(
            map(item => this.mapMaintenanceRecord(item))
        );
    }

    createMaintenanceAsset(assetType: string, item: any): Observable<any> {
        const params = new HttpParams().set('asset_type', assetType);
        return this.http.post<any>(`${this.apiUrl}/maintenance`, item, { params }).pipe(
            map(item => this.mapMaintenanceRecord(item))
        );
    }

    updateMaintenanceAsset(assetType: string, id: number, item: any): Observable<any> {
        const params = new HttpParams().set('asset_type', assetType);
        return this.http.put<any>(`${this.apiUrl}/maintenance/${id}`, item, { params }).pipe(
            map(item => this.mapMaintenanceRecord(item))
        );
    }

    deleteMaintenanceAsset(assetType: string, id: number): Observable<void> {
        const params = new HttpParams().set('asset_type', assetType);
        return this.http.delete<void>(`${this.apiUrl}/maintenance/${id}`, { params });
    }

    // Helper to map backend schema to frontend model
    // Backend already returns lowercase id/deleted, but handle both cases for safety
    private mapMaintenanceRecord(item: any): any {
        if (!item) return item;
        return {
            ...item,
            id: item.id ?? item.ID,
            deleted: item.deleted ?? (item.DELETED === 1)
        };
    }

    // Upload 
    uploadAssetFile(assetType: string, assetId: number, file: File): Observable<any> {
        const formData = new FormData();
        formData.append('asset_type', assetType);
        formData.append('asset_id', assetId.toString());
        formData.append('file', file);
        return this.http.post<any>(`${this.apiUrl}/upload`, formData);
    }

    // Coyote geometry proxy (WKT + derived lat/lng)
    getAssetGeometry(assetCode: string, assetType: string): Observable<{
        shape?: string | null;
        latitude?: number | null;
        longitude?: number | null;
        [key: string]: unknown;
    }> {
        const params = new HttpParams()
            .set('asset_code', assetCode)
            .set('asset_type', assetType);
        return this.http.get<{
            shape?: string | null;
            latitude?: number | null;
            longitude?: number | null;
            [key: string]: unknown;
        }>(`${this.apiUrl}/geometry`, { params });
    }

    upsertAssetGeometry(assetCode: string, assetType: string, shape: string): Observable<{
        shape?: string | null;
        latitude?: number | null;
        longitude?: number | null;
        [key: string]: unknown;
    }> {
        const params = new HttpParams()
            .set('asset_code', assetCode)
            .set('asset_type', assetType);
        return this.http.put<{
            shape?: string | null;
            latitude?: number | null;
            longitude?: number | null;
            [key: string]: unknown;
        }>(`${this.apiUrl}/geometry`, { shape }, { params });
    }

    /**
     * Download filtered asset rows as CSV or Excel (server-streamed; excludes image).
     * `assetType` is the UI singular key (e.g. 'pavement'); mapped to API slug internally.
     * When `columns` is provided, only those fields are exported (FE table order/headers).
     */
    exportAssets(
        assetType: string,
        format: AssetExportFormat,
        filters: ColumnFilters = {},
        columns?: AssetExportColumn[]
    ): Observable<Blob> {
        const endpoint = ASSET_TYPE_TO_API_ENDPOINT[assetType];
        if (!endpoint) {
            throw new Error(`Unknown asset type for export: ${assetType}`);
        }
        let params = this.buildFilterParams(filters).set('format', format);
        if (columns?.length) {
            params = params.set('columns', JSON.stringify(columns));
        }
        return this.http.get(`${this.apiUrl}/${endpoint}/export`, {
            params,
            responseType: 'blob'
        });
    }
}
